import { marketplaceApi, type ProductReview, type ProductReviews } from '../../api/marketplace';
import { apiErrorMessage } from '../../lib/api';
import { haptic, hapticImpact } from '../../lib/telegram';
import { useCallback, useEffect, useState } from 'react';
import './ReviewsBlock.css';

export function RatingStars({
  value,
  size = 16,
  onChange,
}: {
  value: number;
  size?: number;
  onChange?: (rating: number) => void;
}) {
  const rounded = Math.round(value);

  return (
    <span className={`rating-stars${onChange ? ' rating-stars--pick' : ''}`} aria-label={`${value} из 5`}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = star <= rounded;
        const icon = (
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 2.5l2.9 6.1 6.6.9-4.8 4.7 1.2 6.6L12 17.6 6.1 20.8l1.2-6.6L2.5 9.5l6.6-.9L12 2.5Z"
              fill={filled ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={filled ? 0 : 1.6}
            />
          </svg>
        );

        if (!onChange) {
          return (
            <span key={star} className={`rating-stars__star${filled ? ' rating-stars__star--on' : ''}`}>
              {icon}
            </span>
          );
        }

        return (
          <button
            key={star}
            type="button"
            className={`rating-stars__star${filled ? ' rating-stars__star--on' : ''}`}
            aria-label={`${star} из 5`}
            onClick={() => {
              hapticImpact('light');
              onChange(star);
            }}
          >
            {icon}
          </button>
        );
      })}
    </span>
  );
}

export function ReviewForm({
  productId,
  orderId,
  initial,
  onSaved,
  compact,
}: {
  productId: string;
  orderId?: string;
  initial?: ProductReview | null;
  onSaved?: (review: ProductReview) => void;
  compact?: boolean;
}) {
  const [rating, setRating] = useState(initial?.rating ?? 5);
  const [body, setBody] = useState(initial?.body ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRating(initial?.rating ?? 5);
    setBody(initial?.body ?? '');
  }, [initial?.id, initial?.rating, initial?.body]);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);

    try {
      const review = await marketplaceApi.saveReview({
        productId,
        rating,
        body: body.trim() || null,
        ...(orderId ? { orderId } : {}),
      });
      haptic('success');
      onSaved?.(review);
    } catch (cause) {
      haptic('error');
      setError(apiErrorMessage(cause, 'Не удалось сохранить отзыв'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className={`review-form${compact ? ' review-form--compact' : ''}`}
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <p className="review-form__label">{initial ? 'Изменить отзыв' : 'Оставить отзыв'}</p>
      <RatingStars value={rating} size={compact ? 18 : 22} onChange={setRating} />
      <textarea
        className="review-form__body"
        rows={compact ? 3 : 4}
        maxLength={1000}
        placeholder="Как прошла покупка? Необязательно"
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      {error ? <p className="review-form__error">{error}</p> : null}
      <button type="submit" className="review-form__submit" disabled={busy}>
        {busy ? 'Сохраняю…' : initial ? 'Обновить' : 'Опубликовать'}
      </button>
    </form>
  );
}

export function ReviewsBlock({ productId }: { productId: string }) {
  const [data, setData] = useState<ProductReviews | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    marketplaceApi
      .reviews(productId)
      .then(setData)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить отзывы');
      });
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  if (error && !data) {
    return (
      <section className="reviews">
        <p className="reviews__error">{error}</p>
      </section>
    );
  }

  if (!data) return null;

  return (
    <section className="reviews">
      <header className="reviews__head">
        <h2 className="reviews__title">Отзывы</h2>
        {data.ratingCount > 0 ? (
          <p className="reviews__summary">
            <RatingStars value={data.ratingAvg ?? 0} />
            <span>
              {data.ratingAvg} · {data.ratingCount}
            </span>
          </p>
        ) : (
          <p className="reviews__summary">Пока нет отзывов</p>
        )}
      </header>

      {data.canReview ? (
          <ReviewForm
          productId={productId}
          initial={data.myReview}
          onSaved={() => load()}
        />
      ) : null}

      {data.items.length === 0 && !data.canReview ? (
        <p className="reviews__empty">Отзывы появятся после первых покупок</p>
      ) : null}

      <ul className="reviews__list">
        {data.items.map((review) => (
          <li key={review.id} className="reviews__item">
            <div className="reviews__item-head">
              <span className="reviews__author">{review.author}</span>
              <RatingStars value={review.rating} size={12} />
            </div>
            {review.body ? <p className="reviews__body">{review.body}</p> : null}
            <p className="reviews__date">{new Date(review.createdAt).toLocaleDateString('ru-RU')}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
